<template>
  <div class="absolute inset-0 flex items-start justify-center overflow-y-auto bg-slate-200 px-2 py-8 sm:items-center">
    <BaseCard no-padding class="w-screen max-w-sm">
      <div class="flex flex-1 flex-col p-8">
        <h3 class="text-xl font-bold capitalize text-slate-900">
          <ShieldCheckIcon v-if="presentation.success" class="-mt-1 mr-2 inline-block h-6 w-6 text-green-600" aria-hidden="true"/>
          <ShieldExclamationIcon v-else class="-mt-1 mr-2 inline-block h-6 w-6 text-amber-600" aria-hidden="true"/>
          <span>{{ presentation.title }}</span>
        </h3>
        <dl class="mt-2 flex flex-grow flex-col items-start justify-between">
          <dt class="sr-only">Description</dt>
          <dd class="text-sm text-slate-500">
            <span>{{ presentation.message }}</span>
          </dd>
        </dl>
        <FormButton size="tall" class="mt-6" color="gradient" @click="closeWindow">Close Window</FormButton>
      </div>
    </BaseCard>
  </div>
</template>

<script setup>
import BaseCard from "../components/BaseCard.vue";
import FormButton from "../components/FormButton.vue";
import {
  buildIntegrationAuthCompletePayload,
  getIntegrationAuthPresentation,
  getIntegrationAuthToast,
  INTEGRATION_AUTH_COMPLETE_STORAGE_KEY,
} from "../helpers/IntegrationAuthSignals.js";
import {ShieldCheckIcon, ShieldExclamationIcon} from "@heroicons/vue/20/solid/index.js";
import {useRoute, useRouter} from "vue-router";
import {onMounted} from "vue";
import {useToast} from "vue-toastification";

import {visibilityReturnPath} from "../composables/UseIndeedVisibilityConnection.js";
const route = useRoute();
const visibilityIntent = route.query.intent === "visibility";
const returnPath = visibilityReturnPath(route.query.return_to);

const success = !!route.meta?.connected || false;
const integrationName = route.meta?.integration || "account";
const presentation = getIntegrationAuthPresentation({
  integrationName,
  success,
  reconnected: route.query.reconnected === "1",
  connectionPreserved: route.query.connection_preserved === "1",
  error: route.query.error,
});
const router = useRouter();
const toast = useToast();
const integrationService = `${route.params?.service || (visibilityIntent ? "indeed" : "")}`;
const POPUP_WINDOW_NAME = "oauth";
let openerNotified = false;

const shouldTreatAsPopup = () => {
  if (window.opener && window.opener !== window) return true;
  if (window.name === POPUP_WINDOW_NAME) return true;

  return !!window.history?.length && window.history.length <= 2;
};

const notifyOpener = () => {
  if (openerNotified) return;
  openerNotified = true;

  const payload = buildIntegrationAuthCompletePayload({
    service: integrationService,
    status: presentation.status,
    success: presentation.success,
    message: presentation.message,
    authorizationContext: {
      version: route.query.auth_version,
      userId: route.query.auth_user_id,
      companyId: route.query.auth_company_id,
      authorizationRevision: route.query.auth_revision,
    },
  });

  try {
    window.localStorage.setItem(INTEGRATION_AUTH_COMPLETE_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error(error);
  }

  if (window.opener && window.opener !== window && !window.opener.closed) {
    window.opener.postMessage(payload, window.location.origin);
  }
};

const fallbackToIntegrations = () => {
  if (window.closed) return;

  if (visibilityIntent || route.query.return_to) {
    router.push(returnPath);
    return;
  }

  router.push({
    path: "/integrations",
    query: integrationService ? {select: integrationService} : undefined,
  });
};

const attemptPopupClose = () => {
  window.close();

  window.setTimeout(() => {
    if (window.closed) return;

    try {
      window.open("", "_self");
      window.close();
    } catch (error) {
      console.error(error);
    }

    window.setTimeout(() => {
      if (!window.closed) fallbackToIntegrations();
    }, 150);
  }, 150);
};

const closeWindow = () => {
  notifyOpener();

  if (shouldTreatAsPopup()) {
    attemptPopupClose();
    return;
  }

  fallbackToIntegrations();
};

onMounted(() => {
  const completionToast = getIntegrationAuthToast(presentation);
  toast[completionToast.type](completionToast.message, completionToast.options);
  notifyOpener();

  if (shouldTreatAsPopup()) {
    window.setTimeout(() => {
      closeWindow();
    }, 150);
  }
});
</script>
